/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { getCCMServices, isCCMAddNameFeatureActivated } from '@modules/ccm-user-lib/feature'
import {
  useDeleteCCMCluster,
  useGetCCMClusters,
  usePatchCCMCluster,
} from '@modules/ccm-user-lib/hooks'
import { connectedClustersURL } from '@modules/ccm-user-lib/urls'
import { adaptFromWebAPI } from '@modules/ccm-user-lib/util'
import { addToast } from '@modules/cui/Toasts'
import { FormattedMessage } from '@modules/i18n'

import ClusterTable from './ClusterTable'

import type { FC } from 'react'

const messages = defineMessages({
  success: {
    id: 'ccm.clusters-table.success',
    defaultMessage: 'Success',
  },
  error: {
    id: 'ccm.clusters-table.error',
    defaultMessage: 'Error',
  },
  clusterFailedToDisconnect: {
    id: 'ccm.clusters-table.cluster-failed-to-disconnect',
    defaultMessage: 'Failed to disconnect cluster {clusterName}',
  },
  clusterDisconnected: {
    id: 'ccm.clusters-table.cluster-disconnected',
    defaultMessage: 'Cluster {clusterName} has been successfully disconnected',
  },
  clusterFailedToRename: {
    id: 'ccm.clusters-table.cluster-failed-to-rename',
    defaultMessage: 'Failed to rename cluster {clusterName}',
  },
  clusterRenamed: {
    id: 'ccm.clusters-table.cluster-renamed',
    defaultMessage: 'Cluster {clusterName} has been successfully renamed',
  },
})

const ConnectedClusterTable: FC<{
  hasSearch?: boolean
  limit?: number
}> = ({ hasSearch, limit }) => {
  const { formatMessage } = useIntl()

  const { data, isLoading } = useGetCCMClusters()

  const { mutate: patchCCMCluster, isLoading: isPatching } = usePatchCCMCluster({
    onSuccess: (updatedCluster) => {
      addToast({
        title: formatMessage(messages.success),
        color: 'success',
        iconType: 'check',
        text: formatMessage(messages.clusterRenamed, {
          clusterName: updatedCluster.name || updatedCluster.id,
        }),
      })
    },
    onError: (_, payload) => {
      addToast({
        title: formatMessage(messages.error),
        color: 'danger',
        iconType: 'cross',
        text: formatMessage(messages.clusterFailedToRename, {
          clusterName: payload.id,
        }),
      })
    },
  })

  const clusters = adaptFromWebAPI(data)

  const onClusterAddOrUpdateName = (clusterId: string, name: string) => {
    patchCCMCluster({
      id: clusterId,
      body: {
        name,
      },
    })
  }

  const { mutate: deleteCCMCluster, isLoading: isDeleting } = useDeleteCCMCluster({
    onSuccess: (_, disconnectedCluster) => {
      addToast({
        title: formatMessage(messages.success),
        color: 'success',
        iconType: 'check',
        text: formatMessage(messages.clusterDisconnected, {
          clusterName: disconnectedCluster.id,
        }),
      })
    },
    onError: (_, payload) => {
      addToast({
        title: formatMessage(messages.error),
        color: 'danger',
        iconType: 'cross',
        text: formatMessage(messages.clusterFailedToDisconnect, {
          clusterName: payload.id,
        }),
      })
    },
  })

  const onClusterDisconnect = (clusterId: string) => {
    deleteCCMCluster({ id: clusterId })
  }

  return (
    <Fragment>
      <ClusterTable
        clusters={clusters.slice(0, limit)}
        availableCcmServices={getCCMServices()}
        isCCMAddNameFeatureActivated={isCCMAddNameFeatureActivated()}
        isLoading={isLoading || isPatching || isDeleting}
        onClusterAddName={onClusterAddOrUpdateName}
        onClusterDisconnect={onClusterDisconnect}
        onClusterUpdateName={onClusterAddOrUpdateName}
        showSearchAndFilter={hasSearch}
      />
      {limit && clusters.length > limit && (
        <EuiFlexGroup alignItems='center' justifyContent='center'>
          <EuiFlexItem grow={false}>
            <Link to={connectedClustersURL()}>
              <FormattedMessage
                id='ccm-cluster-list'
                defaultMessage='View all connected clusters'
              />
            </Link>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </Fragment>
  )
}

export default ConnectedClusterTable
